import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTheme } from "../context/ThemeContext";
import api from "../api/axios";

const PAGE_NAME = "Settings";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const createEmptyStat = () => ({
  stat_id: null,
  label: "",
  value: 0,
  suffix: "+",
  status: "active",
});

const lightModeColours = [
  { name: "Champagne Bronze", value: "#9a6a32" },
  { name: "Mocha Brown", value: "#7a4e2a" },
  { name: "Royal Wine", value: "#8b2635" },
  { name: "Emerald Teal", value: "#1f4f46" },
  { name: "Royal Navy", value: "#253b73" },
  { name: "Amethyst", value: "#4b2e83" },
  { name: "Obsidian", value: "#111827" },

  { name: "Imperial Gold", value: "#b88746" },
  { name: "Antique Gold", value: "#a77b42" },
  { name: "Burnished Copper", value: "#b56a47" },
  { name: "Terracotta Clay", value: "#b75c3b" },
  { name: "Ruby Garnet", value: "#982d45" },
  { name: "Mulberry Plum", value: "#6d3557" },
  { name: "Deep Aubergine", value: "#52305f" },
  { name: "Midnight Indigo", value: "#2e356d" },
  { name: "Sapphire Blue", value: "#1f4f8a" },
  { name: "Peacock Teal", value: "#0f5a5c" },
  { name: "Forest Emerald", value: "#1d5b47" },
  { name: "Olive Luxe", value: "#6d6a34" },
  { name: "Charcoal Slate", value: "#374151" },
  { name: "Graphite Black", value: "#1f2937" },
];

const darkModeColours = [
  { name: "Soft Champagne", value: "#d6b16f" },
  { name: "Rose Gold", value: "#d98f74" },
  { name: "Blush Wine", value: "#e06b86" },
  { name: "Mint Emerald", value: "#6ee7c8" },
  { name: "Powder Sapphire", value: "#8fb3ff" },
  { name: "Soft Lavender", value: "#b99cff" },
  { name: "Pearl Silver", value: "#d7dde8" },

  { name: "Royal Gold", value: "#e1c07a" },
  { name: "Golden Sand", value: "#f0c987" },
  { name: "Warm Copper", value: "#e39b78" },
  { name: "Coral Bronze", value: "#f09a7a" },
  { name: "Ruby Rose", value: "#ff7a95" },
  { name: "Orchid Pink", value: "#df9cff" },
  { name: "Lilac Bloom", value: "#c7b0ff" },
  { name: "Periwinkle", value: "#9eb4ff" },
  { name: "Sky Sapphire", value: "#7dc7ff" },
  { name: "Aqua Mint", value: "#79e6d3" },
  { name: "Jade Glow", value: "#7ee0ae" },
  { name: "Soft Olive", value: "#c9d16b" },
  { name: "Moonstone", value: "#c9d3e1" },
  { name: "Crystal Silver", value: "#edf2f7" },
];

const Settings = () => {
  const { theme, toggleMode, changeAccent, resetTheme } = useTheme();
  const [homepageStats, setHomepageStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsSaving, setStatsSaving] = useState(false);

  const presetColours =
    theme.mode === "dark" ? darkModeColours : lightModeColours;

  useEffect(() => {
    const fetchHomepageStats = async () => {
      try {
        setStatsLoading(true);

        const response = await api.get("/admin/products/homepage-stats", {
          headers: getPermissionHeaders("view"),
        });

        setHomepageStats(response.data?.data || []);
      } catch (error) {
        if (error.response?.status !== 403) {
          toast.error(
            error.response?.data?.message ||
              "Failed to load homepage statistics."
          );
        }
      } finally {
        setStatsLoading(false);
      }
    };

    fetchHomepageStats();
  }, []);

  const updateHomepageStat = (index, field, value) => {
    setHomepageStats((previous) =>
      previous.map((stat, statIndex) =>
        statIndex === index ? { ...stat, [field]: value } : stat
      )
    );
  };

  const moveHomepageStat = (index, direction) => {
    setHomepageStats((previous) => {
      const targetIndex = index + direction;

      if (targetIndex < 0 || targetIndex >= previous.length) return previous;

      const reordered = [...previous];
      [reordered[index], reordered[targetIndex]] = [
        reordered[targetIndex],
        reordered[index],
      ];

      return reordered;
    });
  };

  const addHomepageStat = () => {
    setHomepageStats((previous) =>
      previous.length >= 8 ? previous : [...previous, createEmptyStat()]
    );
  };

  const saveHomepageStats = async () => {
    if (homepageStats.some((stat) => !String(stat.label || "").trim())) {
      toast.error("Every homepage statistic needs a label.");
      return;
    }

    try {
      setStatsSaving(true);

      const response = await api.put(
        "/admin/products/homepage-stats",
        { stats: homepageStats },
        { headers: getPermissionHeaders("edit") }
      );

      setHomepageStats(response.data?.data || []);
      toast.success("Homepage statistics saved successfully.");
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error(
          error.response?.data?.message ||
            "Failed to save homepage statistics."
        );
      }
    } finally {
      setStatsSaving(false);
    }
  };

  return (
    <section className="admin-page">
      <div className="page-header">
        <div>
          <p>Control Panel</p>
          <h1>Settings</h1>
          <span>Customise theme, colour and admin appearance.</span>
        </div>

        <button type="button" className="primary-action" onClick={resetTheme}>
          Reset Theme
        </button>
      </div>

      <div className="page-card settings-card">
        <h2 style={{width:'100%'}}>Appearance</h2>

        <div className="setting-row">
          <div style={{width:'100%'}}>
            <strong>Light / Dark Mode</strong>
            <p>Current mode: {theme.mode === "light" ? "Light" : "Dark"}</p>
          </div>

          <label className="theme-switch">
            <input
              type="checkbox"
              checked={theme.mode === "dark"}
              onChange={toggleMode}
            />
            <span className="slider"></span>
          </label>
        </div>

        <div className="setting-row" style={{flexDirection:'column'}}>
          <div>
            <strong>Premium Accent Colour</strong>
            <p>
              {theme.mode === "dark"
                ? "Choose lighter luxury accents for dark mode."
                : "Choose deeper luxury accents for light mode."}
            </p>
          </div>

          <div className="colour-options">
            {presetColours.map((colour) => (
              <button
                key={colour.value}
                type="button"
                title={colour.name}
                className={
                  theme.accent === colour.value
                    ? "colour-dot active"
                    : "colour-dot"
                }
                style={{ backgroundColor: colour.value }}
                onClick={() => changeAccent(colour.value)}
              />
            ))}
          </div>
        </div>

        <div className="theme-preview">
          <div className="preview-navbar">
            <span className="preview-logo">WS</span>
            <strong>Preview Navbar</strong>
          </div>

          <div className="preview-content">
            <button className="primary-action">Primary Button</button>
            <button className="logout-btn">Logout Button</button>
          </div>
        </div>
      </div>

      <div className="page-card homepage-stats-admin-card">
        <div className="homepage-stats-admin-header">
          <div>
            <span>Storefront content</span>
            <h2>Homepage Numbers</h2>
            <p>
              Control the figures shown in the animated "Our Journey in
              Numbers" section. Dragging is replaced with simple arrow controls
              for predictable ordering.
            </p>
          </div>

          <button
            type="button"
            className="ghost-action"
            onClick={addHomepageStat}
            disabled={homepageStats.length >= 8 || statsLoading}
          >
            <i className="pi pi-plus" /> Add number
          </button>
        </div>

        {statsLoading ? (
          <div className="mini-empty">Loading homepage numbers...</div>
        ) : (
          <div className="homepage-stats-admin-list">
            {homepageStats.length === 0 && (
              <div className="mini-empty">
                No homepage numbers yet. Add one to start the section.
              </div>
            )}

            {homepageStats.map((stat, index) => (
              <div
                className="homepage-stat-admin-row"
                key={stat.stat_id || `new-stat-${index}`}
              >
                <span className="homepage-stat-admin-index">{index + 1}</span>

                <label>
                  Label
                  <input
                    value={stat.label}
                    maxLength={120}
                    placeholder="Happy Couples"
                    onChange={(event) =>
                      updateHomepageStat(index, "label", event.target.value)
                    }
                  />
                </label>

                <label>
                  Number
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={stat.value}
                    onChange={(event) =>
                      updateHomepageStat(index, "value", event.target.value)
                    }
                  />
                </label>

                <label>
                  Suffix
                  <input
                    value={stat.suffix}
                    maxLength={12}
                    placeholder="+"
                    onChange={(event) =>
                      updateHomepageStat(index, "suffix", event.target.value)
                    }
                  />
                </label>

                <label>
                  Visibility
                  <select
                    value={stat.status}
                    onChange={(event) =>
                      updateHomepageStat(index, "status", event.target.value)
                    }
                  >
                    <option value="active">Visible</option>
                    <option value="inactive">Hidden</option>
                  </select>
                </label>

                <div className="homepage-stat-admin-actions">
                  <button
                    type="button"
                    className="ghost-action"
                    title="Move up"
                    disabled={index === 0}
                    onClick={() => moveHomepageStat(index, -1)}
                  >
                    <i className="pi pi-arrow-up" />
                  </button>

                  <button
                    type="button"
                    className="ghost-action"
                    title="Move down"
                    disabled={index === homepageStats.length - 1}
                    onClick={() => moveHomepageStat(index, 1)}
                  >
                    <i className="pi pi-arrow-down" />
                  </button>

                  <button
                    type="button"
                    className="ghost-action danger-action"
                    title="Remove"
                    onClick={() =>
                      setHomepageStats((previous) =>
                        previous.filter((_, statIndex) => statIndex !== index)
                      )
                    }
                  >
                    <i className="pi pi-trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="homepage-stats-admin-footer">
          <small>Up to eight figures can be shown on the homepage.</small>

          <button
            type="button"
            className="primary-action"
            disabled={statsLoading || statsSaving}
            onClick={saveHomepageStats}
          >
            {statsSaving ? "Saving..." : "Save homepage numbers"}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Settings;
